import { useMemo, useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Copy, Save, Check, Search } from 'react-feather';
import { useCopyToClipboard } from '@trinity/chat-components/hooks/useCopyToClipboard.js';
import styled from '@emotion/styled';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ReplayIcon from '@mui/icons-material/Replay';
import ScienceIcon from '@mui/icons-material/Science';
import { Menu, MenuItem } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
  GridRowSelectionModel,
  useGridApiRef,
  gridFilteredSortedRowEntriesSelector,
} from '@mui/x-data-grid';
import type { MarketingMessage, MessageGenPhase } from '@trinity/chat';
import { HcpAttributesPopover } from '@trinity/chat-components/components/presentation/HcpAttributesPopover.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { MetricsPopover } from '@trinity/chat-components/components/presentation/MetricsPopover.js';
import { FileLayoutSelector } from '@trinity/chat-components/components/presentation/FileLayoutSelector/index.js';
import {
  MessageGenerationTableActionsValueContext,
  MessageGenerationStateContext,
} from '../../screens/MessageGenSession/Context';
import { SupportingEvidenceModal } from './SupportingEvidenceModal.js';

type ViewMode = 'table' | 'cards';

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
    sessionId?: number;
    onTestMessages?: () => void;
  }
}

const CARDS_PER_PAGE = 12;

function normalizeCategory(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function titleCase(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase());
}

type ConfidenceLevel = 'low' | 'medium' | 'high';

function getConfidenceLevel(value: string): ConfidenceLevel {
  const lower = value.toLowerCase();
  if (lower.includes('high')) return 'high';
  if (lower.includes('medium')) return 'medium';
  return 'low';
}

function formatConfidence(value: string): string {
  const titled = titleCase(value);
  if (titled.toLowerCase().includes('confidence')) return titled;
  return `${titled} Confidence`;
}

const PRELINE_TOOLTIP = { whiteSpace: 'pre-line' as const };

function getMessageId(
  objectiveCategory: string | null | undefined,
  messageCategory: string | null | undefined,
  messageNumber: number,
  messages: MarketingMessage[],
  currentMessage: MarketingMessage
): string {
  // Try objectiveCategory first, then message category, then fallback to 'M'
  const source = objectiveCategory || messageCategory;
  if (!source) return `M${messageNumber}`;
  const firstLetter = source.trim().charAt(0).toUpperCase();

  // Count how many messages of the same category come before this one
  const categoryCount = messages.slice(0, messages.indexOf(currentMessage) + 1).filter((m) => {
    const mSource = objectiveCategory || m.category;
    return mSource && mSource.trim().charAt(0).toUpperCase() === firstLetter;
  }).length;

  return `${firstLetter}${categoryCount}`;
}

function CustomToolbar(props: {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  sessionId?: number;
  onTestMessages?: () => void;
}): React.JSX.Element {
  const { viewMode, onViewModeChange, sessionId, onTestMessages } = props;
  const isCardView = viewMode === 'cards';
  const tableActions = useContext(MessageGenerationTableActionsValueContext);
  const session = useContext(MessageGenerationStateContext);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);

  const savingEnabled = canSaveMessages(tableActions?.phase);

  return (
    <GridToolbarContainer>
      {!isCardView && <GridToolbarColumnsButton />}
      <GridToolbarFilterButton />
      <GridToolbarExport csvOptions={{ fileName: `messages-session-${sessionId}` }} />
      <GridToolbarQuickFilter />
      <ToolbarSpacer />
      <FileLayoutSelector
        selected={viewMode === 'cards' ? 'grid' : 'list'}
        onSelect={(layout) => onViewModeChange(layout === 'grid' ? 'cards' : 'table')}
      />
      <ActionsButton onClick={(e) => setActionsAnchorEl(e.currentTarget)}>Actions</ActionsButton>
      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={() => setActionsAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            if (!tableActions) return;
            const unsavedSelected = (tableActions.selectionModel as string[]).filter((id) => {
              const message = tableActions.messages.find((m) => m.identifier === id);
              return message && !message.saved;
            });
            if (unsavedSelected.length > 0) {
              tableActions.onSaveSelected(unsavedSelected);
            }
            setActionsAnchorEl(null);
          }}
          disabled={
            !tableActions ||
            (() => {
              if (!savingEnabled) return true;
              const unsavedCount = (tableActions.selectionModel as string[]).filter((id) => {
                const message = tableActions.messages.find((m) => m.identifier === id);
                return message && !message.saved;
              }).length;
              return unsavedCount === 0 || !tableActions.sessionId;
            })()
          }
        >
          <PlaylistAddIcon sx={{ fontSize: 18, marginRight: 1 }} />
          {(() => {
            const count = tableActions
              ? (tableActions.selectionModel as string[]).filter((id) => {
                  const message = tableActions.messages.find((m) => m.identifier === id);
                  return message && !message.saved;
                }).length
              : 0;
            return count === 1 ? `Save Message (${count})` : `Save Messages (${count})`;
          })()}
        </MenuItem>
        {onTestMessages && (
          <MenuItem
            onClick={() => {
              onTestMessages();
              setActionsAnchorEl(null);
            }}
            disabled={!tableActions || (tableActions.selectionModel as string[]).length === 0}
          >
            <ScienceIcon sx={{ fontSize: 18, marginRight: 1 }} />
            Test Messages ({tableActions ? (tableActions.selectionModel as string[]).length : 0})
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            const params = new URLSearchParams();
            if (session?.sessionId) params.set('segmentationSessionId', session.sessionId);
            const hasSavedMessages = tableActions?.messages.some((m) => m.saved);
            if (session?.id && hasSavedMessages) params.set('messageGenerationSessionId', String(session.id));
            if (session?.state.segmentIds?.length === 1) params.set('segmentId', session.state.segmentIds[0]);
            const qs = params.toString();
            window.open(`/message-library${qs ? `?${qs}` : ''}`, '_blank');
            setActionsAnchorEl(null);
          }}
        >
          <FormatListBulletedIcon sx={{ fontSize: 18, marginRight: 1 }} />
          View Message Library
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (!tableActions) return;
            await tableActions.onResetSession();
            setActionsAnchorEl(null);
          }}
          disabled={!tableActions}
        >
          <ReplayIcon sx={{ fontSize: 18, marginRight: 1 }} />
          Reset Session
        </MenuItem>
      </Menu>
    </GridToolbarContainer>
  );
}

export interface MessageGenerationTableProps {
  messages: MarketingMessage[];
  isGenerating: boolean;
  sessionId?: number;
  numberOfMessages?: number;
  phase?: MessageGenPhase;
  selectionModel: GridRowSelectionModel;
  onSelectionModelChange: (newSelection: GridRowSelectionModel) => void;
  onSaveSelected?: (messageIdentifiers: string[]) => void;
  isSaving?: boolean;
  objectiveCategory?: string | null;
  onTestMessages?: () => void;
}

/**
 * Check if messages can be saved based on the current phase.
 * Messages can be saved once they have been generated.
 */
function canSaveMessages(phase?: MessageGenPhase): boolean {
  if (!phase) return false;
  const savablePhases: MessageGenPhase[] = [
    'messages_ready',
    'prioritizing',
    'prioritized',
    'curating',
    'curated',
  ];
  return savablePhases.includes(phase);
}

export function MessageGenerationTable({
  messages,
  isGenerating,
  sessionId,
  numberOfMessages = 5,
  phase,
  selectionModel,
  onSelectionModelChange,
  onSaveSelected,
  isSaving = false,
  objectiveCategory = null,
  onTestMessages,
}: MessageGenerationTableProps): React.JSX.Element {
  const savingEnabled = canSaveMessages(phase);
  const copyToClipboard = useCopyToClipboard();

  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const apiRef = useGridApiRef();
  const [cardRows, setCardRows] = useState<MarketingMessage[]>([]);
  const [cardPage, setCardPage] = useState(0);
  const [evidenceMessage, setEvidenceMessage] = useState<MarketingMessage | null>(null);

  const syncCardRows = useCallback(() => {
    if (!apiRef.current?.state) {
      setCardRows(messages);
      return;
    }
    try {
      const entries = gridFilteredSortedRowEntriesSelector(apiRef);
      setCardRows(entries.map((e) => e.model as MarketingMessage));
    } catch {
      setCardRows(messages);
    }
  }, [apiRef, messages]);

  // Sync card rows when switching to card view or when messages change
  useEffect(() => {
    if (viewMode === 'cards') {
      const timer = setTimeout(syncCardRows, 0);
      return () => clearTimeout(timer);
    }
  }, [viewMode, messages, syncCardRows]);

  // Sync card rows whenever the DataGrid's internal state changes (filter/sort applied)
  const syncTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleGridStateChange = useCallback(() => {
    if (viewMode !== 'cards') return;
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(syncCardRows, 0);
  }, [viewMode, syncCardRows]);

  const hasRank = useMemo(() => messages.some((m) => m.seqIdx != null), [messages]);

  const totalCardPages = Math.ceil(cardRows.length / CARDS_PER_PAGE);

  // Auto-adjust page if out of bounds
  const validCardPage = Math.min(cardPage, Math.max(0, totalCardPages - 1));

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [];

    // Add checkbox column for selection
    if (savingEnabled) {
      cols.push({
        field: 'select',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;

          // Show skeleton for loading rows
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="18px" $height="18px" />;
          }

          const isSelected = (selectionModel as string[]).includes(row.identifier);

          if (row.saved) {
            return (
              <TableSavedCheckmark title="Already saved">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </TableSavedCheckmark>
            );
          }

          return (
            <TableCheckbox
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                const currentIds = selectionModel as string[];
                if (isSelected) {
                  onSelectionModelChange(currentIds.filter((id) => id !== row.identifier));
                } else {
                  onSelectionModelChange([...currentIds, row.identifier]);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          );
        },
      });
    }

    // Add Message ID column
    cols.push({
      field: 'messageId',
      headerName: 'ID',
      width: 60,
      sortable: false,
      filterable: false,
      valueGetter: (_value: unknown, row: MarketingMessage) => {
        if (row.identifier.startsWith('skeleton-')) return '';
        const messageNumber = (row.seqIdx ?? 0) || messages.indexOf(row) + 1;
        return getMessageId(objectiveCategory, row.category, messageNumber, messages, row);
      },
      renderCell: (params) => {
        const row = params.row as MarketingMessage;
        if (row.identifier.startsWith('skeleton-')) {
          return <SkeletonBox $width="40px" $height="16px" />;
        }
        return <MessageIdCell>{params.value}</MessageIdCell>;
      },
    });

    if (hasRank) {
      cols.push({
        field: 'seqIdx',
        headerName: 'Rank',
        width: 70,
        type: 'number',
      });
    }

    cols.push(
      {
        field: 'headline',
        headerName: 'Message',
        width: 250,
        flex: 1.5,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="90%" $height="14px" />;
          }
          return <TitleCell>{params.value}</TitleCell>;
        },
      },
      {
        field: 'body',
        headerName: 'Description',
        width: 400,
        flex: 3,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="95%" $height="14px" />;
          }
          return <MessageCell>{params.value}</MessageCell>;
        },
      },
      {
        field: 'prioritized',
        headerName: 'Prioritized',
        width: 100,
        valueGetter: (_value: unknown, row: MarketingMessage) => {
          return row.metadata?.prioritization ? 'Yes' : 'No';
        },
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="40px" $height="14px" />;
          }
          const objective = row.metadata?.prioritization?.objective;
          const label = objective ? 'Yes' : 'No';
          return objective ? (
            <Tooltip title={objective} enterDelay={300}>
              <span style={{ cursor: 'default' }}>{label}</span>
            </Tooltip>
          ) : (
            <span>{label}</span>
          );
        },
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 110,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="80px" $height="14px" />;
          }
          return <span>{normalizeCategory(params.value as string)}</span>;
        },
      },
      {
        field: 'confidence',
        headerName: 'Confidence',
        width: 100,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="70px" $height="14px" />;
          }
          const value = row.confidence;
          if (!value) return null;
          const display = titleCase(value);
          const basis = row.confidenceBasis;
          return basis ? (
            <Tooltip title={basis.replaceAll(';', '\n\n')} tooltipstyles={PRELINE_TOOLTIP} enterDelay={500}>
              <span style={{ cursor: 'default' }}>{display}</span>
            </Tooltip>
          ) : (
            <span>{display}</span>
          );
        },
      },
      {
        field: 'doctorNeedAddressed',
        headerName: 'HCP Attributes',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="32px" $height="32px" $borderRadius="6px" />;
          }
          if (!row.doctorNeedAddressed) return null;
          return <HcpAttributesPopover data={row.doctorNeedAddressed} />;
        },
      },
      {
        field: 'curatedFor',
        headerName: 'Curated For',
        width: 180,
        valueGetter: (_value: unknown, row: MarketingMessage) => {
          const segments = row.metadata?.curation?.segments;
          if (!segments || segments.length === 0) return 'N/A';
          return segments.map((s) => s.name).join(', ');
        },
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="80px" $height="14px" />;
          }
          const text = params.value as string;
          return (
            <Tooltip title={text} enterDelay={500}>
              <TooltipCell>{text}</TooltipCell>
            </Tooltip>
          );
        },
      },
      {
        field: 'metrics',
        headerName: 'Metrics',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="32px" $height="32px" $borderRadius="6px" />;
          }
          if (!row.metrics) return null;
          return <MetricsPopover metrics={row.metrics} />;
        },
      },
      {
        field: 'evidence',
        headerName: 'Evidence',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return <SkeletonBox $width="32px" $height="32px" $borderRadius="6px" />;
          }
          return (
            <EvidenceIconButton onClick={() => setEvidenceMessage(row)} title="View Evidence">
              <Search size={14} />
            </EvidenceIconButton>
          );
        },
      },
      {
        field: 'copy',
        headerName: 'Copy',
        width: 70,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row as MarketingMessage;
          if (row.identifier.startsWith('skeleton-')) {
            return null;
          }
          return (
            <CopyIconButton onClick={() => copyToClipboard(`${row.headline}`)} title="Copy message">
              <Copy size={14} />
            </CopyIconButton>
          );
        },
      }
    );

    return cols;
  }, [
    hasRank,
    savingEnabled,
    selectionModel,
    onSelectionModelChange,
    copyToClipboard,
    messages,
    objectiveCategory,
  ]);

  const rows = useMemo(() => messages, [messages]);

  // Generate skeleton rows while loading (always show 9 cards)
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: 9 }).map((_, index) => ({
        identifier: `skeleton-${index}`,
        seqIdx: null,
        headline: '',
        body: '',
        category: '',
        status: 'draft' as const,
        confidence: null,
        confidenceBasis: null,
        doctorNeedAddressed: null,
        metrics: null,
      })),
    []
  );

  const pagedCardRows = useMemo(() => {
    const start = validCardPage * CARDS_PER_PAGE;
    return cardRows.slice(start, start + CARDS_PER_PAGE);
  }, [cardRows, validCardPage]);

  const handleCardSelect = useCallback(
    (identifier: string) => {
      if (!savingEnabled) return;
      const msg = messages.find((m) => m.identifier === identifier);
      if (!msg || msg.saved) return;
      const isSelected = (selectionModel as string[]).includes(identifier);
      if (isSelected) {
        onSelectionModelChange((selectionModel as string[]).filter((id) => id !== identifier));
      } else {
        onSelectionModelChange([...(selectionModel as string[]), identifier]);
      }
    },
    [savingEnabled, messages, selectionModel, onSelectionModelChange]
  );

  const isCardView = viewMode === 'cards';

  // Calculate selected unsaved messages
  const selectedUnsavedIds = useMemo(() => {
    const selectedIds = selectionModel as string[];
    return selectedIds.filter((id) => {
      const msg = messages.find((m) => m.identifier === id);
      return msg && !msg.saved;
    });
  }, [selectionModel, messages]);

  const hasSelection = selectedUnsavedIds.length > 0;

  const handleSaveClick = useCallback(() => {
    if (onSaveSelected && selectedUnsavedIds.length > 0) {
      onSaveSelected(selectedUnsavedIds);
    }
  }, [onSaveSelected, selectedUnsavedIds]);

  const handleClearSelection = useCallback(() => {
    onSelectionModelChange([]);
  }, [onSelectionModelChange]);

  return (
    <Root>
      <DataGridContainer $cardMode={isCardView}>
        <DataGrid
          apiRef={apiRef}
          rows={isGenerating ? skeletonRows : rows}
          columns={columns}
          getRowId={(row) => row.identifier}
          loading={isGenerating}
          checkboxSelection={false}
          disableRowSelectionOnClick
          onStateChange={handleGridStateChange}
          getRowClassName={(params) => (params.row.saved ? 'saved-row' : '')}
          density="compact"
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              viewMode,
              onViewModeChange: setViewMode,
              sessionId,
              onTestMessages,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
            sorting: hasRank ? { sortModel: [{ field: 'seqIdx', sort: 'asc' }] } : undefined,
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8f9fa',
              borderBottom: '2px solid #e0e0e0',
              fontSize: '10px',
              fontWeight: 600,
              color: '#1a1a1a',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              minHeight: '36px !important',
              maxHeight: '36px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '0 8px',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '10px',
              fontWeight: 600,
            },
            '& .MuiDataGrid-row': {
              '&.saved-row': {
                backgroundColor: 'rgba(16, 185, 129, 0.1) !important',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(18, 113, 196, 0.08) !important',
              },
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f9f9f9',
            },
            '& .MuiDataGrid-cell': {
              fontSize: '12px',
              color: '#424242',
              borderBottom: '1px solid #e8e8e8',
              padding: '10px 8px',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiCheckbox-root.Mui-disabled': {
              color: '#d1d5db',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '2px 6px',
              borderBottom: '1px solid #e0e0e0',
              gap: '2px',
              minHeight: '30px',
              alignItems: 'center',
            },
            '& .MuiButton-root': {
              padding: '2px 6px',
              fontSize: '11px',
              minHeight: '24px',
            },
            '& .MuiTextField-root': {
              '& .MuiInputBase-root': {
                fontSize: '11px',
                padding: '0px 2px',
                minHeight: '24px',
              },
              '& .MuiInputBase-input': {
                padding: '2px 6px',
              },
            },
            '& .MuiDataGrid-overlay': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
            },
            ...(isCardView && {
              '& .MuiDataGrid-virtualScroller': { display: 'none' },
              '& .MuiDataGrid-footerContainer': { display: 'none' },
              '& .MuiDataGrid-columnHeaders': { display: 'none' },
            }),
          }}
        />
      </DataGridContainer>
      {isCardView && (
        <CardViewContainer>
          {isGenerating ? (
            <CardGridLayout>
              {skeletonRows.map((skeletonMsg, index) => (
                <SkeletonCard key={skeletonMsg.identifier} />
              ))}
            </CardGridLayout>
          ) : (
            <>
              <CardGridLayout>
                {pagedCardRows.map((msg, index) => (
                  <MessageCard
                    key={msg.identifier}
                    message={msg}
                    hasRank={hasRank}
                    selectable={savingEnabled}
                    selected={(selectionModel as string[]).includes(msg.identifier)}
                    onSelect={handleCardSelect}
                    onViewEvidence={setEvidenceMessage}
                    onCopy={copyToClipboard}
                    onSave={savingEnabled && onSaveSelected ? (id) => onSaveSelected([id]) : undefined}
                    isSaving={isSaving}
                    objectiveCategory={objectiveCategory}
                    messageIndex={validCardPage * CARDS_PER_PAGE + index}
                    allMessages={cardRows}
                  />
                ))}
                {pagedCardRows.length === 0 && (
                  <EmptyCards>No messages match the current filters.</EmptyCards>
                )}
              </CardGridLayout>
              {totalCardPages > 1 && (
                <CardPagination>
                  <PaginationButton disabled={validCardPage === 0} onClick={() => setCardPage((p) => p - 1)}>
                    ← Previous
                  </PaginationButton>
                  <PaginationInfo>
                    Page {validCardPage + 1} of {totalCardPages} ({cardRows.length} messages)
                  </PaginationInfo>
                  <PaginationButton
                    disabled={validCardPage >= totalCardPages - 1}
                    onClick={() => setCardPage((p) => p + 1)}
                  >
                    Next →
                  </PaginationButton>
                </CardPagination>
              )}
            </>
          )}
        </CardViewContainer>
      )}
      <SupportingEvidenceModal
        open={evidenceMessage !== null}
        onClose={() => setEvidenceMessage(null)}
        message={evidenceMessage}
      />
    </Root>
  );
}

// ─── SkeletonCard Component ─────────────────────────────────────────────────

function SkeletonCard(): React.JSX.Element {
  return (
    <Card $saved={false} $selected={false}>
      <CardHeader>
        <CardBadges>
          <SkeletonBox $width="45px" $height="22px" $borderRadius="10px" />
          <SkeletonBox $width="80px" $height="22px" $borderRadius="10px" />
          <SkeletonBox $width="70px" $height="22px" $borderRadius="10px" />
        </CardBadges>
      </CardHeader>
      <SkeletonBox $width="85%" $height="18px" style={{ marginBottom: '8px' }} />
      <SkeletonBox $width="100%" $height="14px" style={{ marginBottom: '4px' }} />
      <SkeletonBox $width="100%" $height="14px" style={{ marginBottom: '4px' }} />
      <SkeletonBox $width="60%" $height="14px" />
      <CardInfoRow style={{ marginTop: '10px' }}>
        <CardInfoItem>
          <SkeletonBox $width="120px" $height="10px" style={{ marginBottom: '4px' }} />
          <SkeletonBox $width="90%" $height="12px" />
        </CardInfoItem>
      </CardInfoRow>
      <CardActions style={{ marginTop: '10px' }}>
        <SkeletonBox $width="90px" $height="26px" $borderRadius="6px" />
        <SkeletonBox $width="70px" $height="26px" $borderRadius="6px" />
      </CardActions>
    </Card>
  );
}

// ─── MessageCard Component ──────────────────────────────────────────────────

interface MessageCardProps {
  message: MarketingMessage;
  hasRank: boolean;
  selectable: boolean;
  onViewEvidence: (message: MarketingMessage) => void;
  selected: boolean;
  onSelect: (identifier: string) => void;
  onCopy: (text: string) => void;
  onSave?: (identifier: string) => void;
  isSaving?: boolean;
  objectiveCategory?: string | null;
  messageIndex: number;
  allMessages: MarketingMessage[];
}

function MessageCard({
  message,
  hasRank,
  selectable,
  selected,
  onSelect,
  onViewEvidence,
  onCopy,
  onSave,
  isSaving = false,
  objectiveCategory,
  messageIndex,
  allMessages,
}: MessageCardProps): React.JSX.Element {
  const need = message.doctorNeedAddressed;
  const hcpText = need ? `${need.themeLabel} / ${need.domainLabel} / ${need.frame}` : null;
  const messageNumber = message.seqIdx ?? messageIndex + 1;
  const messageId = getMessageId(objectiveCategory, message.category, messageNumber, allMessages, message);

  return (
    <Card $saved={message.saved} $selected={selected}>
      <CardHeader>
        <CardBadges>
          <MessageIdBadge>{messageId}</MessageIdBadge>
          {hasRank && message.seqIdx != null && <RankBadge>#{message.seqIdx}</RankBadge>}
          <CategoryBadge>{normalizeCategory(message.category)}</CategoryBadge>
          {message.confidence &&
            (message.confidenceBasis ? (
              <Tooltip
                title={message.confidenceBasis.replaceAll(';', '\n\n')}
                tooltipstyles={PRELINE_TOOLTIP}
                enterDelay={500}
              >
                <ConfidenceBadge $level={getConfidenceLevel(message.confidence)}>
                  {formatConfidence(message.confidence)}
                </ConfidenceBadge>
              </Tooltip>
            ) : (
              <ConfidenceBadge $level={getConfidenceLevel(message.confidence)}>
                {formatConfidence(message.confidence)}
              </ConfidenceBadge>
            ))}
        </CardBadges>
        {selectable && (
          <SelectionIndicator>
            {message.saved ? (
              <Tooltip title="Already saved" enterDelay={300}>
                <SavedCheckmark>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </SavedCheckmark>
              </Tooltip>
            ) : (
              <CardCheckbox
                type="checkbox"
                checked={selected}
                onChange={() => onSelect(message.identifier)}
              />
            )}
          </SelectionIndicator>
        )}
      </CardHeader>
      <CardTitle>{message.headline}</CardTitle>
      <CardBodyRow>
        <CardBody>{message.body}</CardBody>
        <CopyIconButton onClick={() => onCopy(`${message.headline}\n\n${message.body}`)} title="Copy message">
          <Copy size={12} />
        </CopyIconButton>
      </CardBodyRow>
      {hcpText && (
        <CardInfoRow>
          <CardInfoItem>
            <CardInfoLabel>HCP Matched Attributes</CardInfoLabel>
            <Tooltip title={hcpText} enterDelay={500}>
              <CardInfoValue $truncate>{hcpText}</CardInfoValue>
            </Tooltip>
          </CardInfoItem>
        </CardInfoRow>
      )}
      {(message.metadata?.prioritization || message.metadata?.curation) && (
        <CardInfoRow>
          {message.metadata.prioritization && (
            <CardInfoItem style={{ flex: 'none', width: '20%' }}>
              <CardInfoLabel>Prioritized</CardInfoLabel>
              {message.metadata.prioritization.objective ? (
                <Tooltip title={message.metadata.prioritization.objective} enterDelay={500}>
                  <CardInfoValue>Yes</CardInfoValue>
                </Tooltip>
              ) : (
                <CardInfoValue>No</CardInfoValue>
              )}
            </CardInfoItem>
          )}
          {message.metadata.curation?.segments && message.metadata.curation.segments.length > 0 && (
            <CardInfoItem>
              <CardInfoLabel>Curated For</CardInfoLabel>
              <CardInfoValue>
                {message.metadata.curation.segments.map((s) => s.name).join(', ')}
              </CardInfoValue>
            </CardInfoItem>
          )}
        </CardInfoRow>
      )}
      <CardActions>
        {message.metrics && <MetricsPopover metrics={message.metrics} variant="button" />}
        <ViewEvidenceButton onClick={() => onViewEvidence(message)}>Evidence</ViewEvidenceButton>
        <CardActionsSpacer />
        {onSave &&
          (message.saved ? (
            <SavedButton disabled>
              <Check size={12} />
              Saved
            </SavedButton>
          ) : (
            <SaveButton onClick={() => onSave(message.identifier)} disabled={isSaving}>
              <Save size={12} />
              {isSaving ? 'Saving…' : 'Save'}
            </SaveButton>
          ))}
      </CardActions>
    </Card>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
`;

const ToolbarSpacer = styled.div`
  flex: 1;
`;

const ActionsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.palette.primary.main};
  background-image: linear-gradient(to right, #fbfbfb, #f0f2f8);
  border: 2px solid ${(props) => props.theme.palette.primary.main};
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: all 0.3s linear;
  white-space: nowrap;

  &::before {
    position: absolute;
    background-image: linear-gradient(to right, #f0f2f8, #e3f4fb);
    content: '';
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    transition: opacity 0.3s linear;
    opacity: 0;
    border-radius: 8px;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover {
    color: ${(props) => props.theme.palette.primary.dark1};
    border-color: ${(props) => props.theme.palette.primary.dark1};
  }
`;

const DataGridContainer = styled.div<{ $cardMode?: boolean }>`
  ${({ $cardMode }) => ($cardMode ? `flex: none; min-height: auto;` : `flex: 1; min-height: 0;`)}
  position: relative;
  width: 100%;
`;

const MessageIdCell = styled.div`
  font-weight: 600;
  color: #1271c4;
  font-size: 11px;
`;

const TitleCell = styled.div`
  font-weight: 500;
  color: #1a1a1a;
`;

const MessageCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #666;
`;

const TooltipCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
`;

const CategoryBadge = styled.span`
  padding: 4px 10px;
  background: var(--gray-xl);
  color: var(--gray-d);
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: 'draft' | 'approved' | 'published' }>`
  padding: 4px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#fef3c7';
      case 'approved':
        return '#d1fae5';
      case 'published':
        return '#dbeafe';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'draft':
        return '#92400e';
      case 'approved':
        return '#065f46';
      case 'published':
        return '#1e40af';
      default:
        return '#666';
    }
  }};
`;

const TableCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #1271c4;
  margin: 0;
`;

const TableSavedCheckmark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  cursor: default;

  svg {
    opacity: 0.7;
  }
`;

// ─── Card View Styled Components ────────────────────────────────────────────

const CardViewContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
`;

const CardGridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 12px;
`;

const Card = styled.div<{ $saved?: boolean; $selected?: boolean }>`
  border: 1px solid ${({ $saved, $selected }) => ($saved ? '#10b981' : $selected ? '#1271c4' : '#e0e0e0')};
  border-radius: 10px;
  padding: 14px;
  background: ${({ $saved }) => ($saved ? 'rgba(16, 185, 129, 0.05)' : 'white')};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const MessageIdBadge = styled.span`
  padding: 2px 8px;
  background: var(--gray-l);
  color: var(--gray-d);
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;

const RankBadge = styled.span`
  padding: 2px 8px;
  background: #1271c4;
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;

const CONFIDENCE_COLORS: Record<ConfidenceLevel, { bg: string; color: string }> = {
  high: { bg: '#d1fae5', color: '#065f46' },
  medium: { bg: '#fef3c7', color: '#92400e' },
  low: { bg: '#fee2e2', color: '#991b1b' },
};

const ConfidenceBadge = styled.span<{ $level: ConfidenceLevel }>`
  padding: 4px 10px;
  background: ${({ $level }) => CONFIDENCE_COLORS[$level].bg};
  color: ${({ $level }) => CONFIDENCE_COLORS[$level].color};
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  cursor: default;
  white-space: nowrap;
`;

const SelectionIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
`;

const SavedCheckmark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #9ca3af;
  cursor: default;

  svg {
    opacity: 0.7;
  }
`;

const CardCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #1271c4;
  margin: 0;

  &:disabled {
    cursor: default;
    accent-color: #9ca3af;
    opacity: 0.6;
  }
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
  line-height: 1.3;
`;

const CardBody = styled.div`
  font-size: 12px;
  color: #555;
  line-height: 1.5;
`;

const CardBodyRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;

  ${CardBody} {
    flex: 1;
    min-width: 0;
  }
`;

const CardInfoRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
`;

const CardInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const CardInfoLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const CardInfoValue = styled.div<{ $truncate?: boolean }>`
  font-size: 12px;
  color: #424242;
  cursor: default;
  ${({ $truncate }) =>
    $truncate &&
    `overflow: hidden;
     text-overflow: ellipsis;
     white-space: nowrap;`}
`;

const EvidenceIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f0f0f0;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e8e4f3;
    color: #565add;
  }
`;

const ViewEvidenceButton = styled.button`
  padding: 4px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  background: white;
  color: #555;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f0f0f0;
    color: #1271c4;
    border-color: #1271c4;
  }
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
`;

const CardActionsSpacer = styled.div`
  flex: 1;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border: 1px solid #1271c4;
  border-radius: 6px;
  background: #1271c4;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #0f5fa8;
    border-color: #0f5fa8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const SavedButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border: 1px solid #10b981;
  border-radius: 6px;
  background: transparent;
  color: #065f46;
  font-size: 11px;
  font-weight: 500;
  cursor: default;
`;

const CopyIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f0f0f0;
  color: #666;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: #e8e4f3;
    color: #565add;
  }
`;

const CardPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 0 4px;
`;

const PaginationButton = styled.button`
  padding: 4px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  background: white;
  color: #424242;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const PaginationInfo = styled.span`
  font-size: 11px;
  color: #888;
`;

const EmptyCards = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 13px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 200px;
`;

const LoadingText = styled.span`
  font-size: 13px;
  color: #888;
`;

const SkeletonBox = styled.div<{ $width?: string; $height?: string; $borderRadius?: string }>`
  width: ${(props) => props.$width || '100%'};
  height: ${(props) => props.$height || '16px'};
  background: linear-gradient(90deg, rgb(229, 233, 242) 25%, rgb(209, 217, 232) 50%, rgb(229, 233, 242) 75%);
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.$borderRadius || '4px'};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
